import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { StatusApi } from "@/lib/apiClient";
import { Phone, Mail, MapPin, Check, ArrowUpRight, ArrowRight, ExternalLink, Loader2, Warehouse } from "lucide-react";

const ACCENT = "#6E93C7";
const BG = "#3A3A3C";
const BG_DARK = "#2E2E30";
const BORDER = "rgba(255,255,255,0.12)";
const LOGO = "/siacasa-logo.png";
const INTRO_IMG = "/stefano.png";

// La presentazione è servita come percorso sullo stesso dominio dell'app.
function presentazioneUrl() {
  return "/presentazione";
}

export default function PropertyLanding() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [promos, setPromos] = useState([]);
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", privacy: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const token = params.get("t") || "";
    let cancelled = false;
    (async () => {
      let list = [];
      let comp = "";
      const v = await StatusApi.trackVisit(token);
      if (v) {
        comp = v.company_name || "";
        if (Array.isArray(v.promos)) list = v.promos;
      }
      if (list.length === 0 && id) {
        try {
          const p = await StatusApi.getPromo(id);
          if (p) list = [p];
        } catch { /* nessun immobile */ }
      }
      if (!cancelled) {
        setCompany(comp);
        setPromos(list.filter(Boolean));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!loading && promos.length === 0) {
      const url = presentazioneUrl();
      const t = params.get("t");
      window.location.replace(t ? `${url}?t=${t}` : url);
    }
  }, [loading, promos]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name && !form.email && !form.phone) { toast.error("Inserisci almeno nome, email o telefono"); return; }
    if (!form.privacy) { toast.error("Accetta l'informativa privacy per inviare la richiesta"); return; }
    setSending(true);
    const interest = promos.length > 1
      ? `Proposte immobiliari (${promos.length})`
      : `Immobile: ${promos[0]?.title || id || ""}`;
    try {
      await StatusApi.submitFunnelContact({ ...form, token: params.get("t") || "", interest, privacy_consent: true });
      setSent(true);
      toast.success("Richiesta inviata! Ti ricontatto a breve.");
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setSending(false); }
  };

  if (loading || promos.length === 0) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: BG }}>
      <Loader2 className="h-8 w-8 animate-spin text-white/60" /></div>;
  }

  const multi = promos.length > 1;
  const scrollToImmobili = () => document.getElementById("immobili")?.scrollIntoView({ behavior: "smooth" });
  const scrollToForm = () => document.getElementById("richiesta")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen text-white antialiased" style={{ background: BG, fontFamily: "'Avenir Next', 'Segoe UI', Helvetica, Arial, sans-serif" }}>
      <Toaster position="top-center" />
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b" style={{ background: `${BG}D9`, borderColor: BORDER }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <img src={LOGO} alt="SIACASA" className="h-9 w-auto" data-testid="promo-logo" />
          <button onClick={scrollToForm} className="text-sm font-semibold px-5 h-9 inline-flex items-center rounded-none text-white" style={{ background: ACCENT }} data-testid="promo-header-cta">Richiedi info</button>
        </div>
      </header>

      {/* 1) PRESENTAZIONE (prima degli annunci) */}
      <section className="pt-28 sm:pt-32 pb-14 sm:pb-20" data-testid="promo-intro">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.3em] font-bold" style={{ color: ACCENT }}>
              <span className="h-px w-6" style={{ background: ACCENT }} />
              <span className="text-white/60">{company ? `Selezione riservata a ${company} da SIACASA` : "Selezione a lei riservata da SIACASA"}</span>
            </span>
            <h1 className="font-display text-[2.2rem] leading-[1.02] sm:text-5xl font-black tracking-tight mt-5">
              SIACASA — la direzione giusta per il tuo immobile commerciale.
            </h1>
            <p className="text-base sm:text-lg leading-relaxed text-white/70 mt-6 max-w-xl">
              Prima di illustrarle le proposte che abbiamo selezionato per Lei, mi permetto di presentarmi in poche parole.
              Mi chiamo <strong className="text-white">Stefano Tronca</strong> e dal <strong className="text-white">2007</strong> mi occupo
              esclusivamente di immobili commerciali — capannoni, uffici e negozi — tra Brianza e Milano.
              Ecco alcuni dei miei punti fermi operativi:
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {["Specializzazione ed esperienza consolidata nel commerciale", "Rete di aziende e investitori qualificati", "Valutazioni asseverate secondo valori di mercato attuali", "Riservatezza totale sulle trattative"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-white/85 text-sm sm:text-base">
                  <Check className="h-5 w-5 shrink-0 mt-0.5" style={{ color: ACCENT }} /> <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm sm:text-base text-white/60 border-l-2 pl-4 max-w-xl" style={{ borderColor: ACCENT }}>
              <strong className="text-white/85">E per il residenziale?</strong> Ce ne occupiamo con altrettanta cura e professionalità, attraverso un team dedicato specializzato nella divisione residenziale. Pertanto, se hai una casa da vendere, sei comunque nelle mani giuste!
            </p>
            <div className="mt-8">
              <Button onClick={scrollToImmobili} data-testid="promo-intro-cta"
                className="rounded-none h-12 px-7 text-base font-semibold text-white transition-transform active:scale-95 hover:brightness-110" style={{ background: ACCENT }}>
                {multi ? "Vedi le offerte a te riservate" : "Vedi l'offerta a te riservata"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -top-3 -left-3 h-full w-full border" style={{ borderColor: ACCENT }} />
              <img src={INTRO_IMG} alt="Stefano Tronca" className="relative w-full h-[320px] sm:h-[440px] object-cover object-top" data-testid="promo-intro-photo" />
            </div>
          </div>
        </div>
      </section>

      {/* 2) ANNUNCI — sempre a card (una sola se l'invio è singolo) */}
      <div id="immobili" style={{ background: BG_DARK }}>
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="promo-grid">
          <div className="mb-10">
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold" style={{ color: ACCENT }}>
              {multi ? "Proposte immobiliari" : "Proposta immobiliare"}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-3" data-testid="promo-title">
              {multi
                ? `${promos.length} immobili selezionati${company ? ` per ${company}` : " per lei"}`
                : (company ? `Un immobile selezionato per ${company}` : "L'immobile selezionato per lei")}
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl">
              {multi ? "Per ogni immobile può vedere l'annuncio completo." : "Di seguito i dettagli dell'immobile; può vedere l'annuncio completo."}
            </p>
          </div>
          <div className={multi ? "grid gap-6 md:grid-cols-2 lg:grid-cols-3" : "grid gap-6 max-w-md"}>
            {promos.map((p, i) => (
              <div key={p.id || i} data-testid={`promo-card-${i}`}
                className="border flex flex-col overflow-hidden transition-transform hover:-translate-y-1" style={{ borderColor: BORDER, background: BG }}>
                {p.image ? (
                  <img src={p.image} alt={p.title || "Immobile"} className="w-full h-52 object-cover" />
                ) : (
                  <div className="w-full h-52 flex items-center justify-center text-white/30"><Warehouse className="h-10 w-10" /></div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-xl font-bold leading-tight">{p.title || "Immobile commerciale"}</h3>
                  {p.price && <div className="inline-block mt-3 self-start text-white text-sm font-bold px-3 py-1" style={{ background: ACCENT }}>{p.price}</div>}
                  {p.description && <p className={`text-sm mt-3 flex-1 text-white/70 ${multi ? "line-clamp-4" : "whitespace-pre-line"}`}>{p.description}</p>}
                  {p.source_url && (
                    <div className="mt-5">
                      <a href={p.source_url} target="_blank" rel="noopener noreferrer" data-testid={`promo-card-link-${i}`}
                        onClick={() => StatusApi.trackClick(params.get("t") || "", p.source_url)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: ACCENT }}>
                        <ExternalLink className="h-4 w-4" /> Vedi l'annuncio
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* 3) RICHIESTA */}
      <section id="richiesta" style={{ background: BG }}>
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-16">
          <h2 className="font-display text-3xl font-black tracking-tighter text-center">Richiedi informazioni</h2>
          <p className="text-white/60 mt-3 text-center">Niente spam: i tuoi dati non verranno mai ceduti a terzi e li useremo solo per risponderti a questa richiesta.</p>
          {sent ? (
            <div className="mt-8 border p-8 text-center" style={{ borderColor: ACCENT, background: BG_DARK }} data-testid="promo-success">
              <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full" style={{ background: ACCENT }}><Check className="h-6 w-6 text-white" /></div>
              <p className="mt-4 font-display text-xl font-bold">Richiesta ricevuta!</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 border p-6 space-y-3" style={{ borderColor: BORDER, background: BG_DARK }} data-testid="promo-form">
              <Input data-testid="promo-name" value={form.name} onChange={set("name")} placeholder="Nome e cognome" className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG }} />
              <Input data-testid="promo-phone" value={form.phone} onChange={set("phone")} placeholder="Telefono" className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG }} />
              <Input data-testid="promo-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG }} />
              <Textarea data-testid="promo-message" value={form.message} onChange={set("message")} rows={3} placeholder="Messaggio (facoltativo)" className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG }} />
              <label className="flex items-start gap-2.5 text-xs text-white/60 leading-relaxed cursor-pointer">
                <input type="checkbox" data-testid="promo-privacy" checked={form.privacy}
                  onChange={(e) => setForm((f) => ({ ...f, privacy: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-[#6E93C7] shrink-0" />
                <span>Autorizzo SIACASA a utilizzare i miei dati per rispondere a questa richiesta. Nessuna cessione a terzi. *</span>
              </label>
              <Button type="submit" disabled={sending} data-testid="promo-submit" className="w-full h-11 rounded-none text-white font-semibold hover:brightness-110" style={{ background: ACCENT }}>
                {sending ? "Invio..." : <>Invia richiesta <ArrowUpRight className="ml-1.5 h-4 w-4" /></>}
              </Button>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t py-8 text-center text-white/40 text-sm" style={{ borderColor: BORDER, background: BG }}>
        © {new Date().getFullYear()} SIACASA Immobiliare · La direzione giusta
      </footer>
    </div>
  );
}
